import React, { useState } from "react";
import axios from "axios";
import { useAuth } from "./AuthContext";
import { useLanguage } from "./i18n";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "./components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./components/ui/dialog";
import { Settings, Edit, Trash2 } from "lucide-react";
import { toast } from "sonner";

const UserProfile = () => {
  const { user, API, token, logout } = useAuth();
  const { t } = useLanguage();
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const [isEditProfileOpen, setIsEditProfileOpen] = useState(false);
  const [isDeleteAccountOpen, setIsDeleteAccountOpen] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [newFullName, setNewFullName] = useState(user?.full_name || '');
  const [newEmail, setNewEmail] = useState(user?.email || '');
  const [confirmEditPassword, setConfirmEditPassword] = useState('');
  const [deletePassword, setDeletePassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) { toast.error(t('passwordMismatch')); return; }
    if (newPassword.length < 6) { toast.error(t('passwordTooShort')); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/change-password`, { old_password: oldPassword, new_password: newPassword }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t('passwordChangedSuccessfully'));
      setIsChangePasswordOpen(false);
      setOldPassword(''); setNewPassword(''); setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorChangingPassword'));
    } finally { setLoading(false); }
  };

  const handleUpdateProfile = async () => {
    if (!confirmEditPassword) { toast.error('يرجى إدخال كلمة المرور للتأكيد'); return; }
    setLoading(true);
    try {
      const response = await axios.put(`${API}/api/auth/profile`, { full_name: newFullName || null, email: newEmail || null, password: confirmEditPassword }, { headers: { Authorization: `Bearer ${token}` } });
      localStorage.setItem('auth_user', JSON.stringify(response.data));
      toast.success(t('updatedSuccessfully'));
      setIsEditProfileOpen(false); setConfirmEditPassword('');
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    } finally { setLoading(false); }
  };

  const handleDeleteAccount = async () => {
    if (!deletePassword) { toast.error('يرجى إدخال كلمة المرور للتأكيد'); return; }
    setLoading(true);
    try {
      await axios.delete(`${API}/api/auth/account`, { headers: { Authorization: `Bearer ${token}` }, data: { password: deletePassword } });
      toast.success('تم حذف الحساب بنجاح');
      logout();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'خطأ في حذف الحساب');
    } finally { setLoading(false); }
  };

  return (
    <Card className="shadow-lg border-blue-200">
      <CardHeader className="bg-blue-100">
        <CardTitle className="flex items-center gap-2 text-blue-900">
          <Settings className="w-5 h-5" />
          {t('accountSettings')}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6">
        <div className="space-y-4">
          <div className="bg-blue-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600">{t('username')}</p>
            <p className="font-semibold text-blue-900">{user?.username}</p>
            <p className="text-xs text-gray-500 mt-1">{t('cannotChange')}</p>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600">{t('fullName')}</p>
            <p className="font-semibold text-blue-900">{user?.full_name || t('notSpecified')}</p>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600">{t('email')}</p>
            <p className="font-semibold text-blue-900">{user?.email || t('notSpecified')}</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => { setNewFullName(user?.full_name || ''); setNewEmail(user?.email || ''); setIsEditProfileOpen(true); }} className="flex-1 bg-green-600 hover:bg-green-700">
              <Edit className="w-4 h-4 mr-2" />{t('editProfile')}
            </Button>
            <Button onClick={() => setIsChangePasswordOpen(true)} className="flex-1 bg-blue-600 hover:bg-blue-700">
              <Settings className="w-4 h-4 mr-2" />{t('changePassword')}
            </Button>
          </div>
          <div className="mt-6 pt-4 border-t border-red-200">
            <Button onClick={() => setIsDeleteAccountOpen(true)} variant="outline" className="w-full text-red-600 border-red-300 hover:bg-red-50">
              <Trash2 className="w-4 h-4 mr-2" />حذف الحساب نهائياً
            </Button>
          </div>
        </div>

        {/* Edit Profile Dialog */}
        <Dialog open={isEditProfileOpen} onOpenChange={setIsEditProfileOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>{t('personalInfo')}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><label className="block text-sm font-medium mb-2">{t('fullName')}</label><Input type="text" value={newFullName} onChange={(e) => setNewFullName(e.target.value)} placeholder={t('fullName')} /></div>
              <div><label className="block text-sm font-medium mb-2">{t('email')}</label><Input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder={t('enterEmail')} /></div>
              <div><label className="block text-sm font-medium mb-2 text-red-600">كلمة المرور للتأكيد *</label><Input type="password" value={confirmEditPassword} onChange={(e) => setConfirmEditPassword(e.target.value)} placeholder="أدخل كلمة المرور للتأكيد" /></div>
              <div className="flex gap-2">
                <Button onClick={handleUpdateProfile} disabled={loading} className="flex-1 bg-green-600 hover:bg-green-700">{loading ? t('saving') : t('saveChanges')}</Button>
                <Button onClick={() => { setIsEditProfileOpen(false); setConfirmEditPassword(''); }} variant="outline" className="flex-1">{t('cancel')}</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Delete Account Dialog */}
        <Dialog open={isDeleteAccountOpen} onOpenChange={setIsDeleteAccountOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle className="text-red-600">حذف الحساب نهائياً</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200"><p className="text-red-800 text-sm">تحذير: سيتم حذف حسابك نهائياً ولن تتمكن من استعادته. هل أنت متأكد؟</p></div>
              <div><label className="block text-sm font-medium mb-2 text-red-600">أدخل كلمة المرور للتأكيد *</label><Input type="password" value={deletePassword} onChange={(e) => setDeletePassword(e.target.value)} placeholder="كلمة المرور" /></div>
              <div className="flex gap-2">
                <Button onClick={handleDeleteAccount} disabled={loading} className="flex-1 bg-red-600 hover:bg-red-700">{loading ? 'جاري الحذف...' : 'حذف الحساب نهائياً'}</Button>
                <Button onClick={() => { setIsDeleteAccountOpen(false); setDeletePassword(''); }} variant="outline" className="flex-1">إلغاء</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Change Password Dialog */}
        <Dialog open={isChangePasswordOpen} onOpenChange={setIsChangePasswordOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>{t('changePassword')}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><label className="block text-sm font-medium mb-2">{t('oldPassword')}</label><Input type="password" value={oldPassword} onChange={(e) => setOldPassword(e.target.value)} placeholder={t('enterOldPassword')} /></div>
              <div><label className="block text-sm font-medium mb-2">{t('newPassword')}</label><Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder={t('enterNewPassword')} /></div>
              <div><label className="block text-sm font-medium mb-2">{t('confirmPassword')}</label><Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder={t('reenterPassword')} /></div>
              <div className="flex gap-2">
                <Button onClick={handleChangePassword} disabled={loading} className="flex-1 bg-blue-600 hover:bg-blue-700">{loading ? t('saving') : t('save')}</Button>
                <Button onClick={() => setIsChangePasswordOpen(false)} variant="outline" className="flex-1">{t('cancel')}</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default UserProfile;
